const defaultNow = () => (
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
);

export function binaryPayloadByteLength(payload) {
  if (typeof payload === 'string') {
    return typeof TextEncoder !== 'undefined'
      ? new TextEncoder().encode(payload).byteLength
      : payload.length;
  }
  if (payload instanceof ArrayBuffer) return payload.byteLength;
  if (ArrayBuffer.isView(payload)) return payload.byteLength;
  if (Number.isFinite(payload?.size)) return Math.max(0, payload.size);
  if (Number.isFinite(payload?.byteLength)) return Math.max(0, payload.byteLength);
  return 0;
}

/**
 * Collects content-free measurements for the first network synchronization of
 * one Yjs document. Callers decide which updates came from the network or the
 * local IndexedDB provider so the two sources never get conflated.
 */
export function createInitialYjsSyncMetrics({ now = defaultNow } = {}) {
  const startedAtMs = now();
  let completedSnapshot = null;
  let networkReceivedBytes = 0;
  let networkAppliedUpdateBytes = 0;
  let localAppliedUpdateBytes = 0;
  let projectionFlushCount = 0;
  let projectionFullFlushCount = 0;
  let projectionIncrementalFlushCount = 0;
  let projectionTotalDurationMs = 0;
  let projectionMaxDurationMs = 0;

  const canRecord = () => completedSnapshot === null;
  const addBytes = (current, payload) => current + binaryPayloadByteLength(payload);

  const recordProjection = (projectionMetrics) => {
    if (!canRecord() || !projectionMetrics) return false;
    const durationMs = Number(projectionMetrics.durationMs);
    if (!Number.isFinite(durationMs) || durationMs < 0) return false;
    projectionFlushCount += 1;
    projectionTotalDurationMs += durationMs;
    projectionMaxDurationMs = Math.max(projectionMaxDurationMs, durationMs);
    if (projectionMetrics.mode === 'full') projectionFullFlushCount += 1;
    if (projectionMetrics.mode === 'incremental') projectionIncrementalFlushCount += 1;
    return true;
  };

  const complete = ({ nodeCount = 0, relationshipCount = 0 } = {}) => {
    if (completedSnapshot) return completedSnapshot;
    completedSnapshot = Object.freeze({
      completed: true,
      durationMs: Math.max(0, now() - startedAtMs),
      networkReceivedBytes,
      networkAppliedUpdateBytes,
      localAppliedUpdateBytes,
      projectionFlushCount,
      projectionFullFlushCount,
      projectionIncrementalFlushCount,
      projectionTotalDurationMs,
      projectionMaxDurationMs,
      nodeCount: Math.max(0, Number(nodeCount) || 0),
      relationshipCount: Math.max(0, Number(relationshipCount) || 0),
    });
    return completedSnapshot;
  };

  return Object.freeze({
    recordNetworkReceived(payload) {
      if (!canRecord()) return false;
      networkReceivedBytes = addBytes(networkReceivedBytes, payload);
      return true;
    },
    recordNetworkAppliedUpdate(update) {
      if (!canRecord()) return false;
      networkAppliedUpdateBytes = addBytes(networkAppliedUpdateBytes, update);
      return true;
    },
    recordLocalAppliedUpdate(update) {
      if (!canRecord()) return false;
      localAppliedUpdateBytes = addBytes(localAppliedUpdateBytes, update);
      return true;
    },
    recordProjection,
    complete,
    getCompletedSnapshot: () => completedSnapshot,
  });
}
